import { useId } from 'react';
import { Box, Typography } from '@mui/material';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import { ContentHighlights } from '@optimus/models';
import { TestIds } from '../../../assets/testIds';

interface ContentArticleHighlightsProps {
  highlights?: ContentHighlights | null;
}

export default function ContentArticleHighlights({ highlights }: ContentArticleHighlightsProps) {
  const titleId = useId();
  const items = highlights?.items?.filter((item) => item.text?.trim()) ?? [];

  if (!items.length) return null;

  return (
    <Box
      component="section"
      data-testid={TestIds.contentArticleView.highlightsSection}
      aria-labelledby={titleId}
      sx={(theme) => ({
        p: 2,
        borderRadius: '4px',
        // Figma uses an inside stroke, which does not add to the content height.
        boxShadow: `inset 0 0 0 1px ${theme.palette.brand.secondary}`,
        bgcolor: theme.palette.common.white,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        minWidth: 0,
        overflowWrap: 'anywhere',
      })}
    >
      <Box sx={(theme) => ({
        display: 'flex', alignItems: 'center', gap: 1, color: theme.palette.brand.primary,
      })}>
        <ErrorOutline sx={{ fontSize: 24, flexShrink: 0 }} />
        <Typography
          component="h2"
          data-testid={TestIds.contentArticleView.highlightsTitle}
          id={titleId}
          sx={(theme) => ({
            fontFamily: theme.typography.bodyMd.fontFamily,
            fontSize: 13,
            fontWeight: 600,
            lineHeight: '15px',
          })}
        >
          {highlights?.title?.trim() || 'Key Takeaways'}
        </Typography>
      </Box>
      <Box component="ul" role="list" sx={(theme) => ({
        m: 0,
        p: 0,
        listStyle: 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
        color: theme.palette.text.primary,
        fontFamily: theme.typography.bodyMd.fontFamily,
        fontSize: 13,
        lineHeight: '15px',
        '& li': { display: 'flex', gap: 1, whiteSpace: 'pre-line' },
      })}>
        {items.map((item, index) => (
          <li key={item.id ?? index} data-testid={TestIds.contentArticleView.highlightsItem}>
            <Box component="span" aria-hidden="true" sx={{ flexShrink: 0 }}>•</Box>
            <Box component="span" sx={{ minWidth: 0 }}>{item.text}</Box>
          </li>
        ))}
      </Box>
    </Box>
  );
}
